import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { BASE } from '../services/api';
import { moedaInput, moedaNumero, moedaDeNumero } from '../services/moeda';
import { porRotulo } from '../services/ordenar';
import { descricaoImovel, edificioDe } from '../services/imovelLabel';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import '../assets/css/PropostasEfetivas.css';
import '../assets/css/PropostasEfetivasPolish.css';

const VAZIO = {
  codigo_imovel: '', imovel_endereco: '', bairro: '', tipo: '', numero: '',
  bloco: '', complemento: '', quartos: '', vagas: '', area: '',
  valor: '', forma_pagamento: '', valor_permuta: '', descricao_permuta: '',
  fechamento_7_dias: false, probabilidade_fechamento: '',
  situacao: 'em_analise', cliente: '', observacao: '', data_proposta: '', data_fechamento: '',
  id_corretor: '', id_visita: '', id_gerente: '', gerente_nome: '',
};

const dataBR = (v) => {
  if (!v) return '—';
  const d = String(v).length > 10 ? new Date(v) : new Date(`${v}T00:00:00`);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('pt-BR');
};

export function formularioProposta(item) {
  if (!item) return { ...VAZIO };
  return {
      codigo_imovel: item.codigo_imovel || '', imovel_endereco: item.imovel_endereco || '',
      bairro: item.bairro || '', tipo: item.tipo || '', numero: item.numero || '',
      bloco: item.bloco || '', complemento: item.complemento || '',
      quartos: item.quartos || '', vagas: item.vagas || '', area: item.area || '',
      valor: moedaDeNumero(item.valor),
      forma_pagamento: item.forma_pagamento || '',
      valor_permuta: moedaDeNumero(item.valor_permuta),
      descricao_permuta: item.descricao_permuta || '', situacao: item.situacao || 'em_analise',
      cliente: item.cliente || '', observacao: item.observacao || '',
      data_proposta: item.data_proposta || '',
      data_fechamento: item.data_fechamento || '',
      fechamento_7_dias: !!item.fechamento_7_dias,
      probabilidade_fechamento: item.probabilidade_fechamento || '',
      id_corretor: item.id_corretor || '', id_visita: item.id_visita || '',
      id_gerente: item.id_gerente || '',
      gerente_nome: item.gerente_nome || '',
    };
}

/** Editor único usado pela Gestão e pelo painel de Tarefas. */
export default function PropostaEditor({ proposta, onClose, onSaved, children }) {
  const { idCorretor } = useAuth();
  const toast = useToast();
  const [form, setForm] = useState(() => formularioProposta(proposta));
  const editandoId = proposta?.id;
  const [salvando, setSalvando] = useState(false);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [podeEditar, setPodeEditar] = useState(false);
  const [opcoes, setOpcoes] = useState({ formas_pagamento: [], situacoes: [], probabilidades_fechamento: [] });
  const [corretores, setCorretores] = useState([]);
  const [gerentes, setGerentes] = useState([]);
  const [buscaImovel, setBuscaImovel] = useState('');
  const [imoveis, setImoveis] = useState([]);
  const [buscandoImovel, setBuscandoImovel] = useState(false);
  const [visitas, setVisitas] = useState([]);
  const [buscandoVisita, setBuscandoVisita] = useState(false);
  const ehPermuta = form.forma_pagamento === 'permuta';
  const fechar = () => { if (!salvando) onClose(); };
  useEffect(() => {
    let active = true;
    async function carregarOpcoes() {
      try {
        const r = await fetch(`${BASE}/propostas?solicitante_id=${encodeURIComponent(idCorretor)}`);
        const d = await r.json();
        if (!r.ok || d.ok === false) throw new Error(d.error || 'Erro ao carregar opções');
        if (!active) return;
        setOpcoes({ ...d.opcoes, situacoes: (d.opcoes?.situacoes || []).filter(s => !['ativos', 'inativos'].includes(s.value)) });
        if (d.escopo?.edita) {
          const rp = await fetch(`${BASE}/propostas/corretores?solicitante_id=${encodeURIComponent(idCorretor)}`);
          const pessoas = await rp.json();
          if (!rp.ok || pessoas.ok === false) throw new Error(pessoas.error || 'Erro ao carregar responsáveis');
          if (!active) return;
          setCorretores(porRotulo(pessoas.itens || [], 'nome'));
          setGerentes(porRotulo(pessoas.gerentes || [], 'nome'));
          setPodeEditar(true);
        }
      } catch (e) { if (active) setErro(e.message); }
      finally { if (active) setCarregando(false); }
    }
    carregarOpcoes();
    return () => { active = false; };
  }, [idCorretor]);
  const gerentesOpcoes = useMemo(() => {
    if (!form.id_gerente || gerentes.some(g => g.id === form.id_gerente)) return gerentes;
    return [...gerentes, { id: form.id_gerente, nome: form.gerente_nome || form.id_gerente }];
  }, [gerentes, form.id_gerente, form.gerente_nome]);
  // Visitas candidatas: com código do imóvel, todas as visitas dele (qualquer equipe);
  // sem código, as do corretor escolhido ou da equipe.
  const puxarVisitas = useCallback(async () => {
    setBuscandoVisita(true);
    try {
      const qs = new URLSearchParams({ solicitante_id: idCorretor || '' });
      if (form.id_corretor) qs.set('corretor', form.id_corretor);
      if (form.codigo_imovel) qs.set('codigo', form.codigo_imovel);
      const r = await fetch(`${BASE}/propostas/visitas?${qs.toString()}`);
      const d = await r.json();
      if (!r.ok || d.ok === false) throw new Error(d.error || 'Erro ao buscar visitas');
      setVisitas(d.itens || []);
      if (!(d.itens || []).length) toast('Nenhuma visita encontrada para esse filtro.', 'error');
    } catch (e) {
      toast(e.message, 'error');
    } finally {
      setBuscandoVisita(false);
    }
  }, [idCorretor, form.id_corretor, form.codigo_imovel, toast]);

  // Busca por código (catálogo) ou endereço (Imoview). `todos=1` inclui imóvel ainda não
  // publicado no site — sem isso o 12624, disponível, não aparecia para ninguém.
  const buscarImovel = useCallback(async () => {
    const termo = buscaImovel.trim();
    if (!/^\d+$/.test(termo) && termo.length < 3) { toast('Digite o código ou ao menos 3 letras do endereço', 'error'); return; }
    setBuscandoImovel(true);
    try {
      const r = await fetch(`${BASE}/imoveis_busca?todos=1&endereco=${encodeURIComponent(termo)}`);
      const d = await r.json();
      setImoveis(d.lista || []);
      if (!(d.lista || []).length) toast('Nenhum imóvel encontrado com esse código ou endereço', 'error');
    } catch {
      toast('Erro ao buscar no Imoview', 'error');
    } finally {
      setBuscandoImovel(false);
    }
  }, [buscaImovel, toast]);

  const escolherImovel = (item) => {
    setForm((f) => ({
      ...f,
      codigo_imovel: String(item.codigo || ''),
      // A proposta nao tem coluna de edificio; sem juntar aqui, o nome do predio
      // (unico jeito de identificar imovel em Aguas Claras) se perderia no lancamento.
      imovel_endereco: [edificioDe(item), item.endereco || item.titulo || ''].filter(Boolean).join(' — '),
      bairro: item.bairro || '',
      // "S/N" e "n/a" são lixo do Imoview em prédio — não vale preencher com isso.
      numero: /^(s\/n|n\/a)$/i.test(String(item.numero || '').trim()) ? '' : (item.numero || ''),
      bloco: item.bloco || '',
      complemento: item.complemento || '',
      tipo: item.tipo || f.tipo,
      quartos: item.quartos || '',
      vagas: item.vagas || '',
      area: item.area || '',
    }));
    setImoveis([]);
    setBuscaImovel('');
  };

  const set = (campo) => (e) => {
    const valor = ['valor', 'valor_permuta'].includes(campo) ? moedaInput(e.target.value) : e.target.value;
    setForm((f) => ({ ...f, [campo]: valor }));
  };

  const salvar = async (e) => {
    e.preventDefault();
    if (salvando || !podeEditar || carregando) return;
    setSalvando(true);
    try {
      const corpo = { ...form, valor: moedaNumero(form.valor), valor_permuta: moedaNumero(form.valor_permuta) };
      const url = editandoId
        ? `${BASE}/propostas/${editandoId}?solicitante_id=${idCorretor}`
        : `${BASE}/propostas?solicitante_id=${idCorretor}`;
      const r = await fetch(url, {
        method: editandoId ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(corpo),
      });
      const d = await r.json();
      if (!r.ok || d.ok === false) throw new Error(d.error || 'Erro ao salvar');
      toast(editandoId ? 'Proposta atualizada!' : 'Proposta lançada!', 'success');
      onSaved();
    } catch (err) {
      toast(err.message || 'Erro ao salvar', 'error');
    } finally {
      setSalvando(false);
    }
  };


  return (
        <div className="pe-modal-bg pe-editor" onClick={fechar}>
          <form aria-label="Editar proposta" className="pe-modal" onClick={(e) => e.stopPropagation()} onSubmit={salvar}>
            <header><h2>{editandoId ? 'Editar proposta' : 'Lançar proposta'}</h2><button type="button" onClick={fechar}>✕</button></header>

            {erro && <p role="alert">{erro}</p>}
            {carregando && <p role="status">Carregando opções da proposta…</p>}
            <fieldset disabled={carregando || salvando || !podeEditar} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
            <div className="pe-imovel-busca">
              <label>Imóvel (código ou endereço)</label>
              <div>
                <input value={buscaImovel} onChange={(e) => setBuscaImovel(e.target.value)} placeholder="Código (ex.: 12624) ou endereço (ex.: SQN 210)" />
                <button type="button" onClick={buscarImovel} disabled={buscandoImovel}>{buscandoImovel ? 'Buscando…' : 'Buscar'}</button>
              </div>
              {imoveis.length > 0 && (
                <ul className="pe-imovel-lista">
                  {imoveis.map((i) => (
                    <li key={i.codigo}><button type="button" onClick={() => escolherImovel(i)}>
                      <strong>#{i.codigo} · {i.tipo || 'Imóvel'}{i.situacao && !/^vago/i.test(i.situacao) ? ` · ${i.situacao}` : ''}</strong>
                      <span className="pe-imovel-end">
                        {descricaoImovel(i)}
                      </span>
                      <em>
                        {[i.quartos ? `${i.quartos} quartos` : '', i.vagas ? `${i.vagas} vagas` : '', i.area ? `${i.area} m²` : '',
                          typeof i.valor === 'number' ? i.valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }) : i.valor,
                          i.finalidade].filter(Boolean).join(' · ')}
                      </em>
                    </button></li>
                  ))}
                </ul>
              )}
            </div>

            <div className="pe-imovel-busca">
              <label>Visita relacionada (opcional)</label>
              <div>
                <button type="button" onClick={puxarVisitas} disabled={buscandoVisita}>{buscandoVisita ? 'Buscando…' : 'Puxar visitas'}</button>
                {form.id_visita
                  ? <span className="pe-visita-escolhida">Visita <b>{form.id_visita}</b> vinculada <button type="button" onClick={() => setForm((f) => ({ ...f, id_visita: '' }))}>remover</button></span>
                  : <span className="pe-visita-dica">{form.codigo_imovel ? `Traz todas as visitas do imóvel ${form.codigo_imovel}, de qualquer equipe` : form.id_corretor ? 'Traz as visitas do corretor escolhido' : 'Traz as visitas da sua equipe — informe o código do imóvel para ver as de todas as equipes'}</span>}
              </div>
              {visitas.length > 0 && (
                <ul className="pe-imovel-lista">
                  {visitas.map((v) => (
                    <li key={v.id_visita}><button type="button" onClick={() => {
                      // Cliente vem da visita: quem assinou a ficha é quem faz a proposta.
                      setForm((f) => ({ ...f, id_visita: v.id_visita, cliente: v.cliente || f.cliente }));
                      setVisitas([]);
                      if (v.cliente) toast(`Cliente "${v.cliente}" preenchido pela visita.`, 'success');
                    }}>
                      <strong>{v.imovel}</strong>
                      <span className="pe-imovel-end">{dataBR(v.data_visita)} · {v.corretor}{v.cliente ? ` · cliente: ${v.cliente}` : ''}</span>
                      {v.proposta_visita ? <em>proposta na visita: {v.proposta_visita}</em> : null}
                    </button></li>
                  ))}
                </ul>
              )}
            </div>

            <div className="pe-grid">
              {/* Só aparece para diretor/administrador — o servidor devolve a lista vazia
                  para gerente, que lança sempre no próprio nome. */}
              {!!gerentes.length && (
                <label className="span2">Gerente responsável pela proposta
                  <select value={form.id_gerente} onChange={set('id_gerente')}>
                    <option value="">Eu mesmo</option>
                    {gerentesOpcoes.map((g) => (
                      <option key={g.id} value={g.id}>{g.nome}{g.team ? ` · ${g.team}` : ''}</option>
                    ))}
                  </select>
                  <small className="pe-hint">
                    Define de quem é a proposta nos relatórios. Escolher o corretor abaixo
                    sobrescreve a equipe pela dele.
                  </small>
                </label>
              )}
              <label className="span2">Lançar no nome de (corretor)
                <select value={form.id_corretor} onChange={(e) => { setForm((f) => ({ ...f, id_corretor: e.target.value, id_visita: '' })); setVisitas([]); }}>
                  <option value="">Sem corretor definido</option>
                  {corretores.map((c) => <option key={c.id} value={c.id}>{c.nome}{c.team ? ` · ${c.team}` : ''}</option>)}
                </select>
              </label>
              <label>Código do imóvel<input value={form.codigo_imovel} onChange={set('codigo_imovel')} placeholder="Ex.: 12343" /></label>
              <label className="span2">Endereço<input value={form.imovel_endereco} onChange={set('imovel_endereco')} /></label>
              <label>Número<input value={form.numero} onChange={set('numero')} /></label>
              <label>Bloco<input value={form.bloco} onChange={set('bloco')} /></label>
              <label>Apto / complemento<input value={form.complemento} onChange={set('complemento')} placeholder="Apto 506" /></label>
              <label>Bairro<input value={form.bairro} onChange={set('bairro')} /></label>
              <label>Tipo<input value={form.tipo} onChange={set('tipo')} placeholder="Apartamento, casa…" /></label>
              <label>Quartos<input value={form.quartos} onChange={set('quartos')} inputMode="numeric" /></label>
              <label>Vagas<input value={form.vagas} onChange={set('vagas')} inputMode="numeric" /></label>
              <label>Área (m²)<input value={form.area} onChange={set('area')} /></label>
              {/* maxLength = 14 dígitos já formatados ("999.999.999.999,99"), o teto de Numeric(14,2). */}
              <label>Valor da proposta *<input value={form.valor} onChange={set('valor')} inputMode="numeric" placeholder="0,00" maxLength={18} required /></label>
              <label>Forma de pagamento
                <select value={form.forma_pagamento} onChange={set('forma_pagamento')}>
                  <option value="">Selecione…</option>
                  {opcoes.formas_pagamento.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                </select>
              </label>
              <label>Fechamento nos próximos 7 dias
                <input type="checkbox" checked={form.fechamento_7_dias}
                  onChange={(e) => setForm((f) => ({ ...f, fechamento_7_dias: e.target.checked }))} />
                <small className="pe-hint">Marque se acredita que a proposta fechará nesse prazo. A marcação é manual.</small>
              </label>
              <label>Probabilidade de fechamento
                <select value={form.probabilidade_fechamento} onChange={set('probabilidade_fechamento')}>
                  <option value="">Não informada</option>
                  {(opcoes.probabilidades_fechamento || []).map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </label>
              <label>Situação
                <select value={form.situacao} onChange={set('situacao')}>
                  {opcoes.situacoes.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </label>
              {ehPermuta && <label>Valor da permuta (2ª proposta) *<input value={form.valor_permuta} onChange={set('valor_permuta')} inputMode="numeric" placeholder="0,00" maxLength={18} required /></label>}
              {ehPermuta && <label className="span2">O que entra na permuta<input value={form.descricao_permuta} onChange={set('descricao_permuta')} placeholder="Ex.: apartamento menor na Asa Sul" /></label>}
              <label>Cliente<input value={form.cliente} onChange={set('cliente')} /></label>
              <label>Data da proposta<input type="date" value={form.data_proposta} onChange={set('data_proposta')} /></label>
              <label>Data de fechamento<input type="date" value={form.data_fechamento} onChange={set('data_fechamento')} /></label>
              <label className="span3">Observação<textarea rows={3} value={form.observacao} onChange={set('observacao')} /></label>
            </div>

            </fieldset>
            {children}
            <footer>
              <button type="button" onClick={fechar}>Cancelar</button>
              <button type="submit" className="pe-cta" disabled={salvando || carregando || !podeEditar}>{salvando ? 'Salvando…' : 'Salvar proposta'}</button>
            </footer>
          </form>
        </div>
  );
}
